/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import { delay } from '@libs/utils/helpers/delay';
import { getFromStorage, setOnStorage } from '@libs/utils/helpers/handleStorage';
import { MMUpdateFilterProps, OrderByFilter } from '../types';

import { MM_STORAGE } from '../constants';
import { useMM } from '../../../services/matchMaker';
import { SHOffer } from '../../../services/sortingHat';
import { MatchMakerCache } from '../../../services/matchMaker/types';
import { useCreditTrack } from '../../../hooks/useCreditTrack';

import { MMResultProps } from '.';

export const useMMResult = ({ token, user }: MMResultProps) => {
  const userFirstName = user?.fullName?.split(' ')[0];
  const formData = getFromStorage<MatchMakerCache>(MM_STORAGE.FORM);

  const { getComparedCards } = useMM({ token });
  const { MMDecisionProcessedEvent } = useCreditTrack();
  const [result, setResult] = useState<SHOffer[]>([]);
  const [monthlyExpense, setMonthlyExpense] = useState<string>(formData?.monthly_expense ?? '');
  const [annualFilter, setAnnualFilter] = useState<boolean>(formData?.annuity ?? false);
  const [benefitFilter, setBenefitFilter] = useState<string>(formData?.benefit ?? 'both');
  const [editExpense, setEditExpense] = useState<boolean>(false);
  const [orderBy, setOrderBy] = useState<OrderByFilter>('approve');
  const [orderedList, setOrderedList] = useState<SHOffer[]>([]);
  const [showShare, setShowShare] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    getResult();
  }, [token]);

  useEffect(() => {
    setOrderBy('approve');
    orderResult('approve');
  }, [result]);

  useEffect(() => {
    const triggerEvent = result.length > 0 && formData && user?.cpf;
    if (triggerEvent) {
      MMDecisionProcessedEvent({
        document: user?.cpf,
        decision: result,
        formData,
      });
    }
  }, [result]);

  async function getResult() {
    setIsLoading(true);

    const data = await getComparedCards();
    if (!data) return setIsLoading(false);

    setResult(data);
    setIsLoading(false);
  }

  async function updateFilters(filters: MMUpdateFilterProps) {
    const cache = getFromStorage<MatchMakerCache>(MM_STORAGE.FORM);
    const updatedCache = { ...cache, ...filters };

    if (filters?.annuity !== undefined) {
      setAnnualFilter(filters.annuity);
    }

    if (filters?.monthly_expense !== undefined) {
      setMonthlyExpense(filters.monthly_expense);
    }

    if (filters?.benefit !== undefined) {
      setBenefitFilter(filters.benefit);
    }

    setOnStorage('session', MM_STORAGE.FORM, updatedCache);
    await delay(150);

    await getResult();
  }

  function toggleEditExpense() {
    setEditExpense((prev) => !prev);
  }

  function orderResult(orderBy: OrderByFilter) {
    // FILTER BASED ON LEGACY LOGIC
    // I DONT KNOW HOW HANDLE WITH BENEFIT ORDER
    setOrderBy(orderBy);
    const approvalChance = {
      'very-high': 4,
      high: 3,
      medium: 2,
      low: 1,
    };

    const isNativeApp = window?.isNativeApp;
    const offersList = isNativeApp ? result : result.filter((offer) => !offer?.matchMaker?.nativeCardApp);

    const compareOffers = (a?: number, b?: number) => {
      return (b || 0) - (a || 0);
    };

    const orderedList = offersList?.sort((a, b) => {
      const miles = compareOffers(a.matchMaker?.rewardsHighlights.miles, b.matchMaker?.rewardsHighlights.miles);
      const approve = compareOffers(approvalChance[a?.matchMaker?.approvalChance || 'low'], approvalChance[b?.matchMaker?.approvalChance || 'low']);
      const cashback = compareOffers(a.matchMaker?.rewardsHighlights.cashback, b.matchMaker?.rewardsHighlights.cashback);

      switch (orderBy) {
        case 'approve':
          if (approve !== 0) return approve;
          if (cashback !== 0) return cashback;
          if (miles !== 0) return miles;
          return 0;
        case 'miles':
          if (miles !== 0) return miles;
          if (approve !== 0) return approve;
          if (cashback !== 0) return cashback;
          return 0;
        default:
          if (cashback !== 0) return cashback;
          if (miles !== 0) return miles;
          if (approve !== 0) return approve;

          return 0;
      }
    });

    return setOrderedList(orderedList);
  }

  function toggleShare() {
    setShowShare((prev) => !prev);
  }

  return {
    orderBy,
    showShare,
    isLoading,
    orderedList,
    editExpense,
    annualFilter,
    benefitFilter,
    userFirstName,
    monthlyExpense,
    toggleShare,
    orderResult,
    updateFilters,
    toggleEditExpense,
  };
};
